import { useState } from "react";
import { deriveDeviceAuthWrapKey, normalizeUserCode, wrapChatKey } from "../lib/crypto.js";
import { approveDeviceAuthorization } from "../lib/api.js";
import type { Session } from "../lib/session.js";

interface AuthorizeProps {
  session: Session;
  initialCode: string | null;
  onDone: () => void;
}

const FRIENDLY_ERRORS: Record<string, string> = {
  invalid_or_expired_code: "That code is wrong, already used, or has expired. Ask the other device for a new one.",
  userCode_and_wrappedChatKeyForCode_required: "Enter the code shown on the other device."
};

/**
 * New feature (post-Revision-7): reached via `?code=` (see `App.tsx`) when a
 * not-yet-authenticated device (in practice, `mcp`'s `pnpm --filter mcp
 * login`) is waiting to be approved. Mirrors `Settings.tsx`'s device-link PIN
 * flow's crypto, just running in the opposite direction — here THIS
 * (already logged in) device derives the wrap key and wraps `chatKey`,
 * rather than unwrapping one.
 */
export default function Authorize({ session, initialCode, onDone }: AuthorizeProps) {
  const [code, setCode] = useState(initialCode ?? "");
  const [status, setStatus] = useState<"idle" | "working" | "done" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const normalized = normalizeUserCode(code);

  async function handleAuthorize() {
    setStatus("working");
    setErrorMessage(null);
    try {
      if (normalized.length !== 8) {
        setErrorMessage("Enter the full 8-character code shown on the other device.");
        setStatus("error");
        return;
      }
      const wrapKey = await deriveDeviceAuthWrapKey(normalized);
      const wrappedChatKeyForCode = wrapChatKey(session.chatKey, wrapKey);
      await approveDeviceAuthorization({ userCode: normalized, wrappedChatKeyForCode });
      setStatus("done");
    } catch (err) {
      const message = err instanceof Error ? err.message : "authorize_failed";
      setErrorMessage(FRIENDLY_ERRORS[message] ?? message);
      setStatus("error");
    }
  }

  if (status === "done") {
    return (
      <div className="rounded-xl border border-slate-800 bg-gray-900 p-4">
        <h1 className="text-xl font-semibold text-slate-100">Device authorized</h1>
        <p className="mt-2 text-sm text-slate-400">
          The other device should pick this up automatically within a few seconds. You can close this tab or
          go back to Bridge.
        </p>
        <button
          type="button"
          onClick={onDone}
          className="mt-4 cursor-pointer rounded-lg bg-blue-600 px-4 py-2.5 text-white"
        >
          Back to Bridge
        </button>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-slate-800 bg-gray-900 p-4">
      <h1 className="text-xl font-semibold text-slate-100">Authorize a new device</h1>
      <p className="mt-2 text-sm text-slate-400">
        A device (e.g. the <code>bridge</code> MCP tool) wants to connect to your account. Make sure the code
        below matches what's shown there, then authorize.
      </p>
      <label className="mt-4 flex flex-col gap-1 text-sm text-slate-300">
        Code
        <input
          type="text"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          placeholder="XXXX-XXXX"
          autoFocus
          className="w-full rounded-lg border border-slate-700 bg-[#0b1220] px-3 py-2 text-center text-lg tracking-[0.3em] text-slate-200"
        />
      </label>
      <div className="mt-4 flex gap-3">
        <button
          type="button"
          onClick={handleAuthorize}
          disabled={status === "working" || normalized.length !== 8}
          className="cursor-pointer rounded-lg bg-blue-600 px-4 py-2.5 text-white disabled:cursor-not-allowed disabled:opacity-60"
        >
          {status === "working" ? "Authorizing…" : "Authorize"}
        </button>
        <button
          type="button"
          onClick={onDone}
          className="cursor-pointer rounded-lg border border-slate-700 bg-transparent px-4 py-2 text-slate-200"
        >
          Not now
        </button>
      </div>
      {status === "error" && errorMessage && <p className="mt-2 text-sm text-red-400">{errorMessage}</p>}
    </div>
  );
}
